/**
 * Type definitions for the Notification system
 * @module types/notification
 */

import { IUser } from './user';

/**
 * Notification type including both UI and server notifications
 */
export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  LIKE = 'like', 
  COMMENT = 'comment',
  FOLLOW = 'follow',
  MENTION = 'mention',
  SYSTEM = 'system',
  MESSAGE = 'message',
  ACHIEVEMENT = 'achievement',
  ALERT = 'alert'
}

/**
 * Notification priority levels
 */
export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent'
}

/**
 * Available notification delivery methods
 */
export type NotificationDeliveryMethod = 'in_app' | 'email' | 'push' | 'sms';

/**
 * Unique identifier for a notification
 */
export type NotificationId = string;

/**
 * Base notification interface
 */
export interface INotification {
  id: NotificationId;
  type: NotificationType | string;
  message: string;
  title?: string;
  userId?: string;
  read: boolean;
  createdAt: string;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Complete notification interface
 */
export interface Notification extends INotification {
  priority?: NotificationPriority;
  link?: string;
  icon?: string;
  color?: string;
  actor?: IUser;
  expiresAt?: string;
  autoHide?: boolean;
  duration?: number;
  sourceType?: string;
  sourceId?: string;
  postId?: string;
  commentId?: string;
  data?: Record<string, unknown>;
  action?: string;
}

/**
 * Notification settings
 */
export interface NotificationSettings {
  enabled: boolean;
  soundEnabled: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  deliveryMethods: NotificationDeliveryMethod[];
  types: Record<string, boolean>;
  sound: boolean;
  vibration: boolean;
  doNotDisturb: {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
  };
}

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  frequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  grouping: boolean;
  minPriority: NotificationPriority;
  mutedUsers: string[];
  mutedTypes: string[];
  customRules: {
    type: string;
    deliveryMethod: NotificationDeliveryMethod;
    priority: NotificationPriority;
  }[];
}

/**
 * API response wrapper
 */
export interface NotificationResponse {
  success: boolean;
  message?: string;
  data: {
    notification: Notification;
  };
}

/**
 * Multiple notifications response
 */
export interface NotificationsResponse {
  success: boolean;
  message?: string;
  data: {
    notifications: Notification[];
    unreadCount: number;
    pagination: {
      total: number;
      page: number;
      limit: number;
      hasMore: boolean;
    };
  };
}

/**
 * Notification action response
 */
export interface NotificationAction {
  success: boolean;
  message?: string;
  data: {
    id: NotificationId;
    action: 'create' | 'read' | 'unread' | 'delete';
    timestamp: string;
    status?: string;
    notification?: Notification;
  };
}

/**
 * Notification update data
 */
export type NotificationUpdateData = Partial<{
  read: boolean;
  expiresAt: string;
  priority: NotificationPriority;
}>;

/**
 * Notification filter options
 */
export interface NotificationFilters {
  type?: string[];
  read?: boolean;
  startDate?: string;
  endDate?: string;
  priority?: string[];
  search?: string;
}

// Aliases for backward compatibility
export type INotificationSettings = NotificationSettings;
export type INotificationPreferences = NotificationPreferences;
export type INotificationResponse = NotificationResponse;
export type INotificationsResponse = NotificationsResponse; 